/**
 * Media architecture – types & constants
 * Single source of truth for media in Farmly.
 */

export type OwnerType =
  | 'place'
  | 'producer'
  | 'producer_shop'
  | 'product'
  | 'event'
  | 'ui'
  | 'area'
  | 'flea_market'
  | 'flea_market_occurrence'
  | 'accommodation'
  | 'curated_page';
export type MediaKind = 'cover' | 'avatar' | 'gallery' | 'og' | 'icon';
export type SeasonKey = 'winter' | 'spring' | 'summer' | 'autumn';
export type MediaBucket = 'private-media' | 'public-media';

export interface MediaAsset {
  id: string;
  owner_type: OwnerType;
  owner_id: string | null;
  draft_session_id: string | null;
  kind: MediaKind;
  bucket: MediaBucket;
  path_320: string | null;
  path_640: string | null;
  path_1280: string | null;
  mime: string | null;
  width: number | null;
  height: number | null;
  size_bytes: number | null;
  alt_text: string | null;
  sort_order: number;
  created_by: string | null;
  created_at: string;
  season_key: SeasonKey | null;
}

// ── Constants ──────────────────────────────────────────
export const VARIANTS = [320, 640, 1280] as const;
export type VariantWidth = (typeof VARIANTS)[number];

export const MAX_PIXELS = 4096;
export const MAX_UPLOAD_MB = 15;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export const ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
] as const;

// ── Path helpers ───────────────────────────────────────

/** Draft path: private-media/tmp/{userId}/{draftSessionId}/{assetId}_{width}.webp */
export function buildTmpPath(
  userId: string,
  draftSessionId: string,
  assetId: string,
  width: VariantWidth,
): string {
  return `tmp/${userId}/${draftSessionId}/${assetId}_${width}.webp`;
}

/** Public path: public-media/{ownerType}/{ownerId}/{kind}/{assetId}_{width}.webp */
export function buildPublicPath(
  ownerType: OwnerType,
  ownerId: string,
  kind: MediaKind,
  assetId: string,
  width: VariantWidth,
): string {
  return `${ownerType}/${ownerId}/${kind}/${assetId}_${width}.webp`;
}
